// Подредба и филтри на телефон.
//
// В режим „мобилни карти" няма `<thead>`, значи няма и меню в колоната: без
// този панел справката на телефон е списък, който не може нито да се подреди,
// нито да се стесни — а точно на телефона човек най-често търси една сметка.
//
// Стои в antd `Popover` по същата причина като ColumnMenu: `SharedAccountGate`
// разпознава отворен antd слой по DOM и не пуска Esc към навигацията „назад".

import { useEffect, useRef, useState } from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  FilterOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import type { Column } from "@tanstack/react-table";

import { ColumnFilterControl } from "./ColumnFilterControl";
import { isEmptyFilterValue } from "./filterFns";
import type { ColumnFilterSpec } from "./types";

type Props<TData> = {
  /** Всички колони на таблицата — скритите на телефон също се сортират. */
  columns: Column<TData, unknown>[];
  /** Фасетите ги има само в клиентски режим. */
  isServer: boolean;
  onClearFilters: () => void;
};

const labelOf = <TData,>(column: Column<TData, unknown>): string =>
  column.columnDef.meta?.label ||
  (typeof column.columnDef.header === "string" ? column.columnDef.header : "") ||
  column.id;

const arrowClass = (active: boolean) =>
  `grid size-8 shrink-0 place-items-center rounded-lg border text-[12px] transition-colors ${
    active
      ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
      : "border-shade-3 bg-surface text-muted"
  }`;

export const MobileControls = <TData,>({
  columns,
  isServer,
  onClearFilters,
}: Props<TData>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const sortable = columns.filter((column) => column.getCanSort());
  const filterable = columns.filter((column) => column.columnDef.meta?.filter);
  const activeFilters = filterable.filter(
    (column) => !isEmptyFilterValue(column.getFilterValue()),
  ).length;

  // Esc затваря ПАНЕЛА и спира събитието — иначе същото натискане минава и
  // през глобалната навигация „назад".
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!sortable.length && !filterable.length) return null;

  const content = (
    <div className="flex max-h-[70vh] w-[264px] flex-col gap-1 overflow-y-auto py-0.5">
      {sortable.length ? (
        <>
          <p className="px-1 pb-1 font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
            {t("dataTable.mobileSortSection").toUpperCase()}
          </p>
          {sortable.map((column) => {
            const direction = column.getIsSorted();
            return (
              <div key={column.id} className="flex items-center gap-1.5 px-1 py-0.5">
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                  {labelOf(column)}
                </span>
                <button
                  type="button"
                  aria-label={`${labelOf(column)} · ${t("dataTable.sortAsc")}`}
                  aria-pressed={direction === "asc"}
                  onClick={() =>
                    direction === "asc"
                      ? column.clearSorting()
                      : column.toggleSorting(false, false)
                  }
                  className={arrowClass(direction === "asc")}
                >
                  <ArrowUpOutlined />
                </button>
                <button
                  type="button"
                  aria-label={`${labelOf(column)} · ${t("dataTable.sortDesc")}`}
                  aria-pressed={direction === "desc"}
                  onClick={() =>
                    direction === "desc"
                      ? column.clearSorting()
                      : column.toggleSorting(true, false)
                  }
                  className={arrowClass(direction === "desc")}
                >
                  <ArrowDownOutlined />
                </button>
              </div>
            );
          })}
        </>
      ) : null}

      {filterable.length ? (
        <>
          <div className="my-1.5 h-px bg-shade-2" />
          <p className="px-1 pb-1 font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
            {t("dataTable.mobileFilterSection").toUpperCase()}
          </p>
          {filterable.map((column) => (
            <div key={column.id} className="flex flex-col gap-1 px-1 pb-2">
              <span className="text-[12px] font-bold text-soft">
                {labelOf(column)}
              </span>
              <ColumnFilterControl
                spec={column.columnDef.meta?.filter as ColumnFilterSpec}
                value={column.getFilterValue()}
                onChange={(value) => column.setFilterValue(value)}
                facets={isServer ? null : (column.getFacetedUniqueValues() ?? null)}
                bounds={
                  isServer
                    ? null
                    : (column.getFacetedMinMaxValues() as [number, number] | null)
                }
              />
            </div>
          ))}
          {activeFilters ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="flex w-full items-center gap-2.5 rounded-lg px-1 py-2 text-left text-[12.5px] font-semibold text-ink transition-colors hover:bg-shade-1"
            >
              <FilterOutlined className="text-[12px] text-muted" />
              {t("dataTable.clearAllFilters")}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={content}
      arrow={false}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-bold transition-colors ${
          activeFilters
            ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
            : "border-shade-3 bg-surface text-soft"
        }`}
      >
        <SortAscendingOutlined className="text-[13px]" />
        {t("dataTable.mobileControls")}
        {activeFilters ? (
          <span className="font-pos-mono text-[11px]">{activeFilters}</span>
        ) : null}
      </button>
    </Popover>
  );
};
